import React, { useEffect, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { PredefinedQt, PredefinedRt } from "../../interfaces/app_types";
import {
  calculateLaserCosting,
  exportLaserCosting,
  getLaserCostingHistory,
  LaserHistoryEntry,
  laserErrorMessage,
  verifyLaserDxfs,
  LaserInputs,
  LaserPayload,
  LaserResult,
  LaserSettings,
  VerifiedDrawing,
} from "../../api/laserCosting";
import "./laserCosting.css";
import { formatTimestamp } from "../../utils/funcs/days";
import SheetNesting from "./SheetNesting";

type DrawingRow = VerifiedDrawing & { id: number; inputs: LaserInputs };
const fields: {
  key: keyof LaserInputs;
  label: string;
  min: number;
  step?: number;
}[] = [
  { key: "thickness", label: "Thickness (mm)", min: 0.001 },
  { key: "quantity", label: "Quantity", min: 1, step: 1 },
  { key: "cut_length", label: "Cut length (mm)", min: 0 },
  { key: "pierces", label: "Pierces", min: 0, step: 1 },
  { key: "speed", label: "Speed (mm/min)", min: 0.001 },
  { key: "beam_density", label: "Beam density", min: 0.001 },
  { key: "laser_rate", label: "Laser rate", min: 0 },
  { key: "raw_material_rate", label: "Material rate (₹/kg)", min: 0 },
  { key: "bending", label: "Bending (₹/piece)", min: 0 },
];
const settingFields: {
  key: keyof LaserSettings;
  label: string;
  min: number;
}[] = [
  { key: "density", label: "Density (g/cm³)", min: 0.001 },
  { key: "profit_percent", label: "Profit (%)", min: 0 },
];
const money = (value: number) =>
  value.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const valueFrom = (value: string): number | "" =>
  value === "" ? "" : Number(value);

// Matches regular costing's VL and L quantity tiers.
export const laserRateForQuantity = (quantity: number | "") =>
  quantity !== "" && quantity < 100 ? 15 : 12;

const LaserCosting = ({
  predefinedQts = [],
  predefinedRts = [],
  optionsLoading = false,
  optionsError = false,
}: {
  predefinedQts?: PredefinedQt[];
  predefinedRts?: PredefinedRt[];
  optionsLoading?: boolean;
  optionsError?: boolean;
}) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<"costing" | "nesting">("costing");
  const nextId = useRef(0);
  const [rows, setRows] = useState<DrawingRow[]>([]);
  const [units, setUnits] = useState("mm");
  const [title, setTitle] = useState("Laser Cutting Costing");
  const [settings, setSettings] = useState<LaserSettings>({
    density: 7.86,
    profit_percent: 7,
  });
  const [busy, setBusy] = useState<"upload" | "calculate" | "export" | null>(
    null
  );
  const [message, setMessage] = useState("");
  const [result, setResult] = useState<LaserResult | null>(null);
  const [history, setHistory] = useState<LaserHistoryEntry[]>([]);
  const [activeHistoryId, setActiveHistoryId] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState("");
  const refreshHistory = async () => {
    try {
      setHistory(await getLaserCostingHistory());
      setHistoryError("");
    } catch (error) {
      setHistoryError(await laserErrorMessage(error));
    }
  };
  useEffect(() => {
    let cancelled = false;
    getLaserCostingHistory()
      .then((entries) => {
        if (!cancelled) setHistory(entries);
      })
      .catch(async (error) => {
        const message = await laserErrorMessage(error);
        if (!cancelled) setHistoryError(message);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const invalidate = () => {
    setResult(null);
    setMessage("");
  };
  const validRows = rows.filter((row) => row.status === "verified");

  const upload = async (files: File[]) => {
    if (!files.length) return;
    if (rows.length + files.length > 30) {
      setMessage("Keep no more than 30 drawings in one costing.");
      return;
    }
    if (
      files.some(
        (file) =>
          !file.name.toLowerCase().endsWith(".dxf") ||
          file.size === 0 ||
          file.size > 10 * 1024 * 1024
      )
    ) {
      setMessage("Choose non-empty DXF files, each no larger than 10 MB.");
      return;
    }
    const names = [
      ...rows.map((row) => row.filename.toLowerCase()),
      ...files.map((file) => file.name.toLowerCase()),
    ];
    if (new Set(names).size !== names.length) {
      setMessage(
        "Duplicate DXF names found. Remove the existing row or rename the drawing before uploading."
      );
      return;
    }
    invalidate();
    setBusy("upload");
    try {
      const drawings = await verifyLaserDxfs(files, units);
      setRows((prev) => [
        ...prev,
        ...drawings.map((drawing) => ({
          ...drawing,
          id: nextId.current++,
          inputs: {
            thickness: "" as const,
            quantity: "" as const,
            cut_length: drawing.cut_length ?? ("" as const),
            pierces: drawing.pierces ?? ("" as const),
            speed: 2500,
            beam_density: 50,
            laser_rate: 12,
            raw_material_rate: "" as const,
            bending: 0,
          },
        })),
      ]);
      setMessage(
        `${
          drawings.filter((drawing) => drawing.status === "verified").length
        } of ${
          drawings.length
        } drawings verified. Enter thickness and rates for each verified drawing.`
      );
    } catch (error) {
      setMessage(await laserErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const payload = (): LaserPayload => ({
    id: activeHistoryId ?? undefined,
    title: title.trim(),
    settings,
    items: validRows.map((row) => ({
      ...row.inputs,
      verification: row.verification ?? "",
    })),
  });
  const updateInput = (
    id: number,
    key: keyof LaserInputs,
    value: number | ""
  ) => {
    invalidate();
    setRows((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              inputs: {
                ...item.inputs,
                [key]: value,
                ...(key === "quantity"
                  ? { laser_rate: laserRateForQuantity(value) }
                  : {}),
              },
            }
          : item
      )
    );
  };
  const calculate = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy("calculate");
    setMessage("");
    setResult(null);
    try {
      setResult(await calculateLaserCosting(payload()));
    } catch (error) {
      setMessage(await laserErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };
  const download = async () => {
    setBusy("export");
    setMessage("");
    try {
      const savedId = await exportLaserCosting(payload());
      setActiveHistoryId(savedId ?? null);
      await refreshHistory();
      setMessage("Laser costing exported and saved to history.");
    } catch (error) {
      setMessage(await laserErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="laser-costing costing-panel">
      <div
        className="costing-mode-switch"
        role="group"
        aria-label="Laser cutting tools"
      >
        <button
          type="button"
          className={`costing-mode-switch__button${
            mode === "costing" ? " is-active" : ""
          }`}
          aria-pressed={mode === "costing"}
          onClick={() => setMode("costing")}
        >
          Laser costing
        </button>
        <button
          type="button"
          className={`costing-mode-switch__button${
            mode === "nesting" ? " is-active" : ""
          }`}
          aria-pressed={mode === "nesting"}
          onClick={() => setMode("nesting")}
        >
          Sheets required
        </button>
      </div>
      <div hidden={mode !== "costing"}>
        {history.length > 0 && (
          <div
            className="costing-history-bar"
            aria-label="Recent laser costings"
          >
            <span className="costing-history-bar__label">Recent:</span>
            {history.map((entry) => (
              <button
                key={entry.id}
                type="button"
                disabled={!!busy}
                className={`costing-history-tab${
                  activeHistoryId === entry.id
                    ? " costing-history-tab--active"
                    : ""
                }`}
                title={`${entry.title} — ${formatTimestamp(entry.timestamp)}`}
                onClick={() => {
                  setActiveHistoryId(entry.id);
                  setTitle(entry.title);
                  setSettings({ ...entry.settings });
                  setRows(
                    entry.items.map((item) => ({
                      ...item,
                      id: nextId.current++,
                      inputs: { ...item.inputs },
                    }))
                  );
                  invalidate();
                  setMessage(
                    `Loaded "${entry.title}". Calculate to refresh unit costs.`
                  );
                }}
              >
                <span className="costing-history-tab__title">
                  {entry.title}
                </span>
                <span className="costing-history-tab__date">
                  {formatTimestamp(entry.timestamp)}
                </span>
              </button>
            ))}
          </div>
        )}
        {historyError && (
          <p role="alert" className="text-danger">
            History: {historyError}{" "}
            <button
              type="button"
              className="costing-sm-btn"
              disabled={!!busy}
              onClick={() => void refreshHistory()}
            >
              Retry
            </button>
          </p>
        )}
        {activeHistoryId && (
          <button
            type="button"
            className="costing-sm-btn mb-2"
            disabled={!!busy}
            onClick={() => {
              setActiveHistoryId(null);
              setRows([]);
              setTitle("Laser Cutting Costing");
              setSettings({
                density: 7.86,
                profit_percent: 7,
              });
              invalidate();
            }}
          >
            New laser costing
          </button>
        )}
        <h2>Laser cutting costing</h2>
        <p>
          Upload one cutting profile per DXF. Outer dimensions come from
          model-space geometry; text, dimensions and hatching are ignored. All
          measurements below are in mm.
        </p>
        <div className="laser-costing__upload">
          <label>
            Drawings without units
            <select
              className="costing-input"
              value={units}
              disabled={!!busy}
              onChange={(event) => setUnits(event.target.value)}
            >
              <option value="mm">Interpret as mm</option>
              <option value="inch">Interpret as inches</option>
            </select>
          </label>
          <input
            ref={fileInput}
            type="file"
            accept=".dxf"
            multiple
            hidden
            aria-label="DXF files"
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.target.value = "";
              void upload(files);
            }}
          />
          <button
            className="btn btn-primary"
            type="button"
            disabled={!!busy}
            onClick={() => fileInput.current?.click()}
          >
            {busy === "upload" ? "Verifying DXFs…" : "Upload multiple DXFs"}
          </button>
          <span>Up to 30 files, 10 MB each.</span>
        </div>
        {message && (
          <p role="status" className="laser-costing__message">
            {message}
          </p>
        )}
        <form onSubmit={calculate}>
          <fieldset disabled={!!busy}>
            {optionsLoading && (
              <p className="costing-hint">
                Loading quantity and material rate options…
              </p>
            )}
            {optionsError && (
              <p role="alert" className="text-danger">
                Could not load costing options. Please reload to try again.
              </p>
            )}
            <div className="laser-costing__settings">
              <label>
                Costing title
                <input
                  className="costing-input"
                  required
                  maxLength={200}
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                />
              </label>
              {settingFields.map((field) => (
                <label key={field.key}>
                  {field.label}
                  <input
                    className="costing-input"
                    type="number"
                    required
                    min={field.min}
                    step="any"
                    value={settings[field.key]}
                    onChange={(event) => {
                      invalidate();
                      setSettings((prev) => ({
                        ...prev,
                        [field.key]: valueFrom(event.target.value),
                      }));
                    }}
                  />
                </label>
              ))}
            </div>
            {rows.map((row) => (
              <article className="laser-costing__drawing" key={row.id}>
                <div className="laser-costing__drawing-header">
                  <div>
                    <strong>{row.filename}</strong>{" "}
                    <span
                      className={
                        row.status === "verified"
                          ? "text-success"
                          : "text-danger"
                      }
                    >
                      {row.status === "verified"
                        ? "Verified"
                        : "Verification failed"}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    aria-label={`Remove ${row.filename}`}
                    onClick={() => {
                      invalidate();
                      setRows((prev) =>
                        prev.filter((item) => item.id !== row.id)
                      );
                    }}
                  >
                    Remove
                  </button>
                </div>
                {row.status === "error" ? (
                  <p className="text-danger" role="alert">
                    {row.error}
                  </p>
                ) : (
                  <>
                    <p>
                      Outer blank:{" "}
                      <strong>
                        {row.length?.toFixed(3)} × {row.width?.toFixed(3)} mm
                      </strong>
                    </p>
                    {row.warnings?.map((warning) => (
                      <p className="costing-hint" key={warning}>
                        {warning}
                      </p>
                    ))}
                    <div className="laser-costing__inputs">
                      {fields.map((field) => (
                        <div key={field.key}>
                          <label htmlFor={`laser-${row.id}-${field.key}`}>
                            {field.label}
                          </label>
                          {field.key === "quantity" ? (
                            <select
                              id={`laser-${row.id}-${field.key}`}
                              className="costing-input form-select"
                              aria-label={`${row.filename} ${field.label}`}
                              required
                              disabled={optionsLoading || optionsError}
                              value={row.inputs.quantity}
                              onChange={(event) =>
                                updateInput(
                                  row.id,
                                  "quantity",
                                  valueFrom(event.target.value)
                                )
                              }
                            >
                              <option value="">Select quantity</option>
                              {predefinedQts.map((option) => (
                                <option key={option.id} value={option.value}>
                                  {option.value}
                                </option>
                              ))}
                            </select>
                          ) : field.key === "raw_material_rate" ? (
                            <div className="input-group">
                              <input
                                id={`laser-${row.id}-${field.key}`}
                                className="form-control costing-input--number"
                                type="number"
                                min={0}
                                step="any"
                                required
                                placeholder="Enter or select rate"
                                aria-label={`${row.filename} ${field.label}`}
                                value={row.inputs.raw_material_rate}
                                onChange={(event) =>
                                  updateInput(
                                    row.id,
                                    "raw_material_rate",
                                    valueFrom(event.target.value)
                                  )
                                }
                              />
                              <Dropdown>
                                <Dropdown.Toggle
                                  variant="outline-secondary"
                                  disabled={
                                    optionsLoading || optionsError || !!busy
                                  }
                                  aria-label={`${row.filename} Material rate options`}
                                />
                                <Dropdown.Menu>
                                  {predefinedRts.map((option) => (
                                    <Dropdown.Item
                                      key={option.id}
                                      onClick={() =>
                                        updateInput(
                                          row.id,
                                          "raw_material_rate",
                                          option.value
                                        )
                                      }
                                    >
                                      {option.value}
                                    </Dropdown.Item>
                                  ))}
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          ) : (
                            <input
                              id={`laser-${row.id}-${field.key}`}
                              className="costing-input"
                              aria-label={`${row.filename} ${field.label}`}
                              type="number"
                              required
                              min={field.min}
                              step={field.step ?? "any"}
                              value={row.inputs[field.key]}
                              onChange={(event) => {
                                updateInput(
                                  row.id,
                                  field.key,
                                  valueFrom(event.target.value)
                                );
                              }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </article>
            ))}
            {rows.some((row) => row.status === "error") && (
              <p className="text-danger">
                Remove or re-upload failed drawings before calculating.
              </p>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                !validRows.length ||
                optionsLoading ||
                optionsError ||
                rows.some((row) => row.status === "error")
              }
            >
              {busy === "calculate"
                ? "Calculating…"
                : "Calculate laser costing"}
            </button>
          </fieldset>
        </form>
        {result && (
          <section
            className="laser-costing__results"
            aria-label="Laser costing results"
          >
            <h3>Costing results</h3>
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    {[
                      "DXF name",
                      "Blank size (mm)",
                      "Weight (kg)",
                      "Material (₹)",
                      "Laser labour (₹)",
                      "Bending (₹)",
                      "Profit (₹)",
                      "Quantity",
                      "Unit cost (₹)",
                    ].map((label) => (
                      <th key={label}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.items.map((row, index) => (
                    <tr key={index}>
                      <td>{row.filename}</td>
                      <td>
                        {row.total_length.toFixed(3)} ×{" "}
                        {row.total_width.toFixed(3)}
                      </td>
                      <td>{row.weight.toFixed(4)}</td>
                      <td>{money(row.material_cost)}</td>
                      <td>{money(row.labour)}</td>
                      <td>{money(Number(row.bending))}</td>
                      <td>{money(row.profit)}</td>
                      <td>{row.quantity}</td>
                      <td>{money(row.unit_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="laser-costing__drawing-header">
              <button
                className="btn btn-success"
                type="button"
                disabled={!!busy}
                onClick={() => void download()}
              >
                {busy === "export" ? "Exporting…" : "Export laser costing"}
              </button>
            </div>
          </section>
        )}
        <p className="costing-hint mt-3">
          Blank weight = (outer L + thickness × 2) × (outer W + thickness × 2) ×
          thickness × density ÷ 1,000,000. Laser labour = (cut length ×
          thickness ÷ speed + pierces × thickness ÷ beam density) × laser rate.
          Profit applies to material + laser labour + bending.
        </p>
      </div>
      <div hidden={mode !== "nesting"}>
        <SheetNesting />
      </div>
    </div>
  );
};

export default LaserCosting;
